// ═══════════════════════════════════════════════════════════════════════════
//  SERVER OUTLET — PC cermin lagu per outlet (21 Okt 2026)
//
//  Tiap outlet punya PC cermin: menarik semua berkas lagu dari PC engine
//  kantor, menyajikannya ke TV room lewat LAN. Halaman ini menunjukkan tiap
//  cermin: outlet, alamat LAN, kapan terakhir aktif, berapa berkas tersalin
//  dari total, dan tombol tarik ulang.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, Server, Trash2, CheckCircle2, AlertTriangle } from 'lucide-react'
import api, { pesanGalat } from '../lib/api'

type Cermin = {
  nama: string; outlet_id: string; outlet: string; media_url: string
  terakhir_at: string; created_at: string; aktif: boolean
  tersalin: number; total_berkas: number
  info?: { versi?: string; os?: string } | null
}

function lalu(iso: string): string {
  const d = (Date.now() - new Date(iso).getTime()) / 1000
  if (d < 60) return 'baru saja'
  if (d < 3600) return `${Math.floor(d / 60)} menit lalu`
  if (d < 86400) return `${Math.floor(d / 3600)} jam lalu`
  return `${Math.floor(d / 86400)} hari lalu`
}

export default function ServerOutlet({ bolehKelola }: { bolehKelola: boolean }) {
  const [rows, setRows] = useState<Cermin[]>([])
  const [galat, setGalat] = useState('')
  const [kabar, setKabar] = useState('')
  const [memuat, setMemuat] = useState(false)

  const muat = useCallback(async () => {
    setMemuat(true); setGalat('')
    try { const r = await api.get<Cermin[]>('/api/cermin'); setRows(r.data ?? []) }
    catch (e) { setGalat(pesanGalat(e, 'Gagal memuat daftar server outlet')) }
    finally { setMemuat(false) }
  }, [])

  useEffect(() => { muat(); const t = setInterval(muat, 30000); return () => clearInterval(t) }, [muat])

  async function ulang(c: Cermin) {
    if (!confirm(`Tarik ulang SEMUA berkas di ${c.outlet} (${c.nama})? Cermin akan memverifikasi tiap berkas dan mengunduh yang kurang/rusak.`)) return
    try { const r = await api.post<{ pesan: string }>(`/api/cermin/${encodeURIComponent(c.nama)}/ulang`); setKabar(r.data.pesan); muat() }
    catch (e) { setGalat(pesanGalat(e)) }
  }
  async function hapus(c: Cermin) {
    if (!confirm(`Hapus pendaftaran ${c.nama} (${c.outlet})? PC-nya tidak dimatikan; dia akan mendaftar lagi sendiri kalau masih jalan.`)) return
    try { await api.delete(`/api/cermin/${encodeURIComponent(c.nama)}`); muat() }
    catch (e) { setGalat(pesanGalat(e)) }
  }

  return (
    <div className="space-y-5">
      <div className="bg-bg-1 border border-border rounded-xl p-4">
        <p className="text-[12.5px] text-text-2 leading-relaxed">
          <b>PC cermin</b> di tiap outlet menarik semua berkas lagu dari PC engine kantor (lewat Tailscale)
          dan menyajikannya ke TV room lewat LAN outlet. TV tidak perlu disetel: server otomatis memberi
          alamat cermin outlet itu. Lagu baru tertarik sendiri tiap ±5 menit.
        </p>
        <p className="text-[11.5px] text-text-3 mt-1.5 leading-relaxed">
          Pasang: PC Windows biasa di LAN outlet + Tailscale, lalu jalankan <code>pasang-cermin-outlet.ps1</code> (pilih outlet, tempel token).
          IP LAN PC harus direservasi di router.
        </p>
      </div>

      <div className="bg-bg-1 border border-border rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <Server size={15} className="text-gold" />
          <span className="font-medium text-[13.5px]">Server lagu per outlet</span>
          <span className="text-[12px] text-text-3">{memuat ? 'memuat…' : `${rows.length} cermin`}</span>
          <button onClick={muat} className="ml-auto text-[11.5px] text-text-3 hover:text-gold inline-flex items-center gap-1">
            <RefreshCw size={12} /> segarkan
          </button>
        </div>
        {galat && <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2 mb-3">{galat}</div>}
        {kabar && <div className="text-[12px] text-gold bg-gold/10 border border-gold/25 rounded-lg px-3 py-2 mb-3">{kabar}</div>}

        {rows.length === 0 && !memuat ? (
          <div className="text-[13px] text-text-3 py-12 text-center leading-relaxed">
            Belum ada PC cermin yang mendaftar.<br />
            <span className="text-[12px]">Setelah <code>pasang-cermin-outlet.ps1</code> dijalankan di PC outlet, barisnya muncul di sini.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-wide text-text-3">
                <tr className="border-b border-border">
                  <th className="text-left font-medium py-2">Outlet</th>
                  <th className="text-left font-medium py-2">PC cermin</th>
                  <th className="text-left font-medium py-2">Alamat LAN</th>
                  <th className="text-left font-medium py-2 w-32">Status</th>
                  <th className="text-left font-medium py-2 w-56">Berkas tersalin</th>
                  {bolehKelola && <th className="text-left font-medium py-2 w-40">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(c => {
                  const persen = c.total_berkas ? Math.round(c.tersalin * 100 / c.total_berkas) : 0
                  return (
                    <tr key={c.nama} className="border-b border-border/60 hover:bg-bg-2/50">
                      <td className="py-2.5 pr-3 font-medium">{c.outlet}</td>
                      <td className="py-2.5 pr-3 text-text-2">{c.nama}<div className="text-[11px] text-text-3">{c.info?.os ?? ''}</div></td>
                      <td className="py-2.5 pr-3 text-[12px] text-text-2 font-mono">{c.media_url}</td>
                      <td className="py-2.5">
                        {c.aktif
                          ? <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border bg-emerald-500/10 text-emerald-400 border-emerald-500/30"><CheckCircle2 size={11} /> aktif · {lalu(c.terakhir_at)}</span>
                          : <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border bg-red-500/10 text-red-400 border-red-500/30"><AlertTriangle size={11} /> mati · {lalu(c.terakhir_at)}</span>}
                      </td>
                      <td className="py-2.5">
                        <div className="text-[12px] text-text-2">{c.tersalin} / {c.total_berkas} ({persen}%)</div>
                        <div className="mt-1 h-1.5 rounded bg-bg-3 overflow-hidden">
                          <div className={`h-full ${persen >= 100 ? 'bg-emerald-500' : 'bg-gold'} transition-all`} style={{ width: `${Math.min(100, persen)}%` }} />
                        </div>
                      </td>
                      {bolehKelola && (
                        <td className="py-2.5">
                          <div className="flex gap-1.5">
                            <button onClick={() => ulang(c)} title="Verifikasi & tarik ulang semua berkas"
                              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-bg-3 border border-border text-text-2 hover:border-gold-2">
                              <RefreshCw size={12} /> Tarik ulang
                            </button>
                            <button onClick={() => hapus(c)} title="Hapus pendaftaran"
                              className="text-[11px] px-2 py-1 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
